import { useCallback, useEffect } from 'react'

import { usePanelOwnedCall } from '#/features/mass-action'
import type { ActiveCallInfo } from '#/features/mass-action'
import {
  endCall,
  useStartCall as useSharedStartCall,
} from '#/shared/lib/telephony'
import type { CallDisabledReason } from '#/shared/lib/telephony'

import { usePhoneCall } from './call'

/**
 * The live Telnyx call as shown inside the phone panel, or null. The panel
 * only owns calls placed from its own dialer — calls started anywhere else
 * (a table/detail phone number, the header) plus incoming and just-ended calls
 * live in the global header pill, not here. Resolved independently of the
 * header pill's picker, so an incoming ring never hides the on-call view.
 */
export function usePanelCall(): ActiveCallInfo | null {
  return usePanelOwnedCall()
}

export type { CallDisabledReason }

export interface StartCall {
  /** Place a call to a number from the active line, then open the call page. */
  call: (number: string) => void
  /** False while telephony connects, another call is live, or the active line
   *  can't place calls (`outbound_call`). */
  ready: boolean
  /** The one gate keeping `ready` false right now; null when ready. */
  disabledReason: CallDisabledReason | null
}

/**
 * The panel's dialer for the keypad, row quick actions and conversation
 * header: the shared `useStartCall` (one dial logic for every surface) plus
 * the panel-only bit — expanding the on-call page when a fresh dial goes out.
 */
export function useStartCall(): StartCall {
  const { call: dial, ready, disabledReason } = useSharedStartCall('panel')
  const expand = usePhoneCall((state) => state.expand)

  const call = useCallback(
    (number: string) => {
      if (dial(number)) expand()
    },
    [dial, expand],
  )

  return { call, ready, disabledReason }
}

/** Hang up the live panel call, clearing it from the UI even if the SIP layer hangs. */
export function endPanelCall(info: ActiveCallInfo): void {
  void endCall(info.session)
}

/** Reset mute/hold + re-expand the full view when the active call changes. */
export function useTrackPanelCall(callId: string | null): void {
  const track = usePhoneCall((state) => state.track)
  useEffect(() => {
    track(callId)
  }, [callId, track])
}
